import { StyleSheet, Platform } from 'react-native';
import type { Theme } from '../../../theme/getTheme';
import { PALETTE } from './CoinStoreModal.constants';

export const coinStoreStyles = (theme: Theme) => StyleSheet.create({
  // ── Modal root ────────────────────────────────────────────────
  flex1: { flex: 1 },
  bgFill: { flex: 1 },
  gradientFill: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 },
  contentWrap: {
    flex: 1,
    ...(Platform.OS === 'web' ? { maxWidth: 540, alignSelf: 'center' as const, width: '100%' } : {}),
  },
  // ── Header ────────────────────────────────────────────────────
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 18,
    paddingBottom: 12,
    zIndex: 5,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: '900',
    color: theme.white,
    letterSpacing: -0.2,
    textShadowColor: 'rgba(0,0,0,0.4)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 0,
  },
  closeBtn: {
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: 'rgba(255,255,255,0.07)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  // ── Wallet chip ───────────────────────────────────────────────
  walletChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 6,
    paddingLeft: 6,
    paddingRight: 14,
    borderRadius: 999,
    backgroundColor: PALETTE.walletChipBg,
    borderWidth: 2,
    borderColor: theme.accent,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 3px 0 rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.08)' }
      : { shadowColor: '#000', shadowOffset: { width: 0, height: 3 }, shadowOpacity: 0.4, shadowRadius: 0, elevation: 4 }),
  },
  walletCoinIcon: { width: 24, height: 24 },
  walletValue: {
    fontSize: 15,
    fontWeight: '900',
    color: theme.accent,
    minWidth: 30,
    textAlign: 'right',
  },
  gainLabel: {
    position: 'absolute',
    right: 8,
    top: -8,
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderRadius: 999,
    backgroundColor: theme.secondary,
  },
  gainLabelText: {
    fontSize: 11,
    fontWeight: '800',
    color: PALETTE.mintInk,
  },
  // ── Segmented control wrap ────────────────────────────────────
  segmentedWrap: {
    paddingHorizontal: 18,
    paddingBottom: 10,
  },
  // ── Scroll body ───────────────────────────────────────────────
  scrollContent: {
    paddingHorizontal: 14,
    paddingBottom: 32,
  },
  // ── Banner ────────────────────────────────────────────────────
  bannerWrap: {
    marginHorizontal: -14,
    overflow: 'hidden',
    borderTopWidth: 1.5,
    borderBottomWidth: 1.5,
    borderColor: `${theme.primary}59`,
  },
  bannerImage: {
    width: '100%',
    height: 150,
  },
  bannerShimmerStrip: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 80,
  },
  bannerShimmerGradient: { flex: 1 },
  // ── Bundle grid ───────────────────────────────────────────────
  grid: {
    marginTop: 18,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  // ── Bundle card ───────────────────────────────────────────────
  bundleCard: {
    borderRadius: 16,
    borderWidth: 2.5,
    borderColor: PALETTE.cardBorder,
    padding: 12,
    paddingBottom: 12,
    alignItems: 'center',
    minHeight: 220,
    overflow: 'visible',
    ...(Platform.OS === 'web'
      ? { boxShadow: `0 4px 0 ${PALETTE.cardShadow}, 0 10px 18px rgba(0,0,0,0.45), inset 0 0 0 2px rgba(255,255,255,0.10)` }
      : { shadowColor: '#000', shadowOffset: { width: 0, height: 6 }, shadowOpacity: 0.45, shadowRadius: 10, elevation: 8 }),
  },
  bundleCardBest: {
    borderColor: `${theme.accent}80`,
    ...(Platform.OS === 'web'
      ? { boxShadow: `0 0 0 2px ${theme.accent}73, 0 4px 0 ${PALETTE.cardShadow}, 0 10px 20px rgba(0,0,0,0.5), 0 0 24px ${theme.accent}59, inset 0 0 0 2px rgba(255,255,255,0.10)` }
      : { shadowColor: theme.accent, shadowOffset: { width: 0, height: 6 }, shadowOpacity: 0.45, shadowRadius: 16, elevation: 12 }),
  },
  bundleCardPressed: { transform: [{ translateY: 2 }], opacity: 0.9 },
  bundleCardHover: { transform: [{ translateY: -2 }] },
  bundleGradientFill: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    borderRadius: 14,
  },
  bundleShadowOverlay: {
    position: 'absolute',
    left: 0, right: 0, bottom: 0,
    height: '60%',
    borderRadius: 14,
  },
  bundleCount: {
    fontSize: 26,
    fontWeight: '900',
    color: theme.white,
    letterSpacing: -0.2,
    lineHeight: 28,
    textShadowColor: 'rgba(29,15,77,0.8)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 2,
  },
  bundleArtWrap: {
    flex: 1,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 80,
    paddingVertical: 6,
    position: 'relative',
  },
  // Art: single coin
  artCoin64: { width: 64, height: 64 },
  // Art: two coins
  artTwoWrap: { width: 88, height: 64 },
  artCoinLarge: { position: 'absolute', left: 4, top: 0, width: 56, height: 56 },
  artCoinSmallRight: { position: 'absolute', right: 0, top: 14, width: 48, height: 48, opacity: 0.82 },
  // Art: three coins
  artThreeWrap: { width: 100, height: 70 },
  artCoinSmallLeft: { position: 'absolute', left: 0, top: 14, width: 48, height: 48, opacity: 0.84 },
  artCoinCenter: { position: 'absolute', left: 26, top: 0, width: 56, height: 56 },
  artCoinSmallRight3: { position: 'absolute', right: 0, top: 14, width: 48, height: 48, opacity: 0.84 },
  // Art: stack / pile
  artStack: { height: 88, width: 88 },
  artPile: { height: 84, width: 100 },
  artPileX: { height: 92, width: 100 },
  artPileXGlow: {
    ...(Platform.OS !== 'web'
      ? { shadowColor: 'rgba(255,255,255,0.5)', shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.6, shadowRadius: 12 }
      : {}),
  },
  bundleOldPrice: {
    fontSize: 12,
    fontWeight: '800',
    color: 'rgba(255,255,255,0.55)',
    textDecorationLine: 'line-through',
    height: 16,
  },
  bundlePriceRow: {
    width: '100%',
    alignItems: 'center',
    gap: 2,
    marginTop: 8,
  },
  bundleBuyBtn: {
    width: '100%',
    paddingVertical: 9,
    paddingHorizontal: 10,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: PALETTE.buyBorder,
    ...(Platform.OS === 'web'
      ? { boxShadow: `0 3px 0 ${PALETTE.buyBorder}, inset 0 1px 0 rgba(255,255,255,0.35)` }
      : { shadowColor: PALETTE.buyBorder, shadowOffset: { width: 0, height: 3 }, shadowOpacity: 1, shadowRadius: 0, elevation: 3 }),
  },
  bundleBuyBtnLoading: { opacity: 0.6 },
  bundleBuyBtnText: {
    fontSize: 17,
    fontWeight: '900',
    color: theme.white,
    letterSpacing: 0.2,
    textShadowColor: PALETTE.buyBorder,
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 0,
  },
  // Best-card shimmer overlay
  bundleShimmerClip: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    borderRadius: 16,
    overflow: 'hidden',
  },
  bundleShimmerStrip: {
    position: 'absolute',
    top: 0, bottom: 0,
    width: 60,
  },
  // Sparkle positions
  sparkle0: { position: 'absolute', top: '4%', right: '10%' },
  sparkle1: { position: 'absolute', top: '22%', left: '8%' },
  sparkle2: { position: 'absolute', bottom: '18%', right: '16%' },
  sparkleTxtLg: { fontSize: 14, color: theme.white },
  sparkleTxtSm: { fontSize: 10, color: theme.white },
  // ── Sticker ───────────────────────────────────────────────────
  stickerWrap: {
    position: 'absolute',
    top: -10,
    right: -10,
    zIndex: 4,
    width: 56,
    height: 56,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stickerSvgAbs: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 },
  stickerTextRotate: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
    transform: [{ rotate: '-10deg' }],
  },
  stickerText: {
    fontSize: 9,
    fontWeight: '900',
    color: theme.white,
    letterSpacing: 0.5,
    textAlign: 'center',
    textShadowColor: 'rgba(0,0,0,0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 0,
  },
  stickerTextBig: {
    fontSize: 14,
    fontWeight: '900',
    color: theme.white,
    letterSpacing: -0.5,
    textAlign: 'center',
    lineHeight: 16,
    textShadowColor: 'rgba(0,0,0,0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 0,
  },
  // ── Earn section ──────────────────────────────────────────────
  sectHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 22,
    marginBottom: 10,
  },
  sectTitle: {
    fontSize: 12,
    fontWeight: '900',
    letterSpacing: 2,
    color: theme.accentLight,
    textTransform: 'uppercase',
  },
  sectMeta: { fontSize: 11, color: theme.gray[500], fontWeight: '700' },
  earnCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 11,
    paddingHorizontal: 13,
    borderRadius: 14,
    backgroundColor: PALETTE.surface,
    borderWidth: 2,
    borderColor: PALETTE.surfaceBorder,
    marginBottom: 9,
    ...(Platform.OS === 'web'
      ? { boxShadow: `0 3px 0 ${PALETTE.dropShadow}, inset 0 1px 0 rgba(255,255,255,0.06)` }
      : { shadowColor: PALETTE.dropShadow, shadowOffset: { width: 0, height: 3 }, shadowOpacity: 1, shadowRadius: 0, elevation: 3 }),
  },
  earnCardPressed: { transform: [{ translateY: 1 }], opacity: 0.9 },
  earnCardText: { flex: 1, minWidth: 0 },
  earnIcon: {
    width: 44,
    height: 44,
    borderRadius: 10,
    backgroundColor: theme.accent,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: PALETTE.earnIconBorder,
    ...(Platform.OS === 'web'
      ? { boxShadow: `0 2px 0 ${PALETTE.earnShadow}, inset 0 1px 0 rgba(255,255,255,0.4)` }
      : { shadowColor: PALETTE.earnShadow, shadowOffset: { width: 0, height: 2 }, shadowOpacity: 1, shadowRadius: 0, elevation: 2 }),
  },
  earnTitle: { fontSize: 14, fontWeight: '800', color: theme.white },
  earnSub: { fontSize: 12, color: theme.gray[500], marginTop: 1, fontWeight: '600' },
  earnCta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 7,
    paddingHorizontal: 12,
    borderRadius: 999,
    backgroundColor: theme.accent,
    borderWidth: 2,
    borderColor: PALETTE.earnIconBorder,
    ...(Platform.OS === 'web'
      ? { boxShadow: `0 2px 0 ${PALETTE.earnShadow}, inset 0 1px 0 rgba(255,255,255,0.4)` }
      : { shadowColor: PALETTE.earnShadow, shadowOffset: { width: 0, height: 2 }, shadowOpacity: 1, shadowRadius: 0, elevation: 2 }),
  },
  earnCtaText: {
    fontSize: 13,
    fontWeight: '900',
    color: PALETTE.earnIconBorder,
  },
  // ── Footer ────────────────────────────────────────────────────
  footer: { marginTop: 14, alignItems: 'center', gap: 4 },
  footerRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  footerText: { fontSize: 11, color: theme.gray[400], lineHeight: 17, textAlign: 'center' },
  // ── Toast ─────────────────────────────────────────────────────
  toast: {
    position: 'absolute',
    alignSelf: 'center',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 10,
    paddingHorizontal: 14,
    paddingLeft: 12,
    borderRadius: 14,
    backgroundColor: PALETTE.surface,
    borderWidth: 2,
    borderColor: theme.secondary,
    maxWidth: 320,
    zIndex: 90,
    ...(Platform.OS === 'web'
      ? { boxShadow: `0 12px 28px rgba(0,0,0,0.6), 0 4px 0 ${PALETTE.dropShadow}` }
      : { shadowColor: '#000', shadowOffset: { width: 0, height: 8 }, shadowOpacity: 0.5, shadowRadius: 14, elevation: 14 }),
  },
  toastCheck: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: theme.secondary,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: PALETTE.mintInk,
  },
  toastLabel: { fontSize: 10, color: theme.gray[400], fontWeight: '800', letterSpacing: 1.5 },
  toastText: { fontSize: 13, fontWeight: '800', color: theme.secondary },
  // ── Particle / confetti layer ─────────────────────────────────
  particleLayer: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
  },
  particle: { position: 'absolute', width: 26, height: 26 },
  particleImg: { width: 26, height: 26 },
  confettiPiece: { position: 'absolute' },
});
